import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/*
  Every type step and semantic role, by class suffix.

  tailwind-merge has to be told these are FONT SIZES. It groups classes by
  prefix, and `text-` is ambiguous — `text-sm` is a size, `text-red-500` is a
  colour. For its own scales it can tell them apart; for custom tokens it cannot,
  so it treated `text-ui` and `text-text-1` as the same group and dropped the
  first one.

  The effect was silent and total: any element whose size and colour arrived
  through one cn() call lost its size and fell back to the inherited 16px. A room
  row's name and preview both rendered at 16px — larger than the 13.4px chat
  header title, which was a plain string and kept its class. That is the whole
  reason the list looked oversized while the header looked small.

  Anything NOT in this list keeps tailwind-merge's default handling, so
  `text-text-2`, `text-accent-text` and friends stay colours.

  Adding a type step or role to packages/theme/tokens.css means adding it here too. A missing
  entry does not error — it silently deletes the size at every cn() call site.
*/
const TYPE_CLASSES = [
  // Raw scale steps.
  "micro",
  "meta",
  "body",
  "ui",
  "subhead",
  "title",
  "page",
  "display",
  "hero",
  /* Narrow-viewport companion to `hero`. Only ever paired with `md:text-hero`. */
  "hero-narrow",
  /* The title face's own ladder (ADR 0011). Read only by the four title roles. */
  "poster-s",
  "poster-m",
  "poster-l",
  "poster-xl",
  // Semantic roles (DESIGN.md §2, ADR 0004).
  "screen-title",
  "panel-title",
  "aside-title",
  "dialog-title",
  "stat-value",
  "post-title",
  "row-name",
  "row-preview",
  "row-meta",
  "nav-item",
  "section-label",
  "field-label",
  "field-value",
  "field-hint",
  "prose",
  "message",
  "control",
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: TYPE_CLASSES }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
